import * as _ from "lodash";
import qs, { ParsedQs } from "qs";
import UrlPattern from "url-pattern";

import { ProcessInstanceStateFilter } from "../model/ProcessInstanceStateFilter";
import { defaultCountsTimeLimitDays, FilterCategory, ListState } from "./ListState";
import { ProcessWithStatus } from "./ProcessWithStatus";

const pathPattern = new UrlPattern("(/:base)/:listType/List(/:environmentName)(/:agentGroupName)(/:processGuid)(/)");
const initialPathParts: PathParts | null = pathPattern.match(window.location.pathname);

interface PathParts {
    listType: string;
    base?: string;
    agentGroupName?: string;
    environmentName?: string;
    processGuid?: string | "instances" | null;
}

export interface AgentGroupEnvironment {
    agentGroupName: string | null;
    environmentName: string | null;
}
export enum ProcessListType {
    Process,
    Subprocess
}

export const basePath = initialPathParts && initialPathParts.base;
const isSubprocessList = initialPathParts && initialPathParts.listType && initialPathParts.listType.toLowerCase() === "subprocess";

export const listType = isSubprocessList ? ProcessListType.Subprocess : ProcessListType.Process;

const getSearchParametersFromString = (searchString: string): ListSearchParameters => {
    const searchParams = qs.parse(searchString);
    const page = parseInt(searchParams["page"] as string, 10);
    const highlightedProcess = parseInt(searchParams["highlightProcess"] as string, 10);

    const filterCategoryString = searchParams["filterCategory"] as string;
    const filterCategory: string | undefined = Object.values(FilterCategory).find((x) => x === filterCategoryString);

    const filterText: IFilterType = searchParams["filterText"] || null;
    const processGuids: IFilterType = searchParams["processGuids"] || null;
    const startTime: IFilterType = searchParams["startTime"] || null;
    const endTime: IFilterType = searchParams["endTime"] || null;

    const stateSearchString = searchParams["instanceListStateFilter"] as string;
    const stateFilter: ProcessInstanceStateFilter | undefined = Object.values(ProcessInstanceStateFilter).find((x) => x === stateSearchString);

    return {
        page: !isNaN(page) ? page : undefined,
        sortBy: searchParams["sortBy"],
        desc: searchParams["desc"] && searchParams["desc"] === "true",
        highlightProcess: !isNaN(highlightedProcess) ? highlightedProcess : undefined,
        filterCategory: filterCategory || FilterCategory.Text,
        filterText: filterCategory && filterText,
        instanceListStateFilter: stateFilter,
        processGuids: processGuids,
        endTime: endTime,
        startTime: startTime
    };
};

export function getAgentGroupFromPath(): AgentGroupEnvironment {
    const pathParts = pathPattern.match(location.pathname);
    const agentGroupName = pathParts.agentGroupName; // if no agent group is passed we do not want to keep that as null. We do checks after we recieve all possilbe agent groups in FetchActions.ts
    const environmentName = pathParts.environmentName || "Default";
    return { environmentName: environmentName, agentGroupName: agentGroupName };
}

export function setAgentGroupToPath(environmentName: string, agentGroupName: string) {
    const location: Location = Object.assign({}, window.location);
    const pathParts = pathPattern.match(location.pathname);
    if (pathParts.environmentName !== environmentName
        || pathParts.agentGroupName !== agentGroupName) {

        pathParts.agentGroupName = agentGroupName;
        pathParts.environmentName = environmentName;
        const newPathname = pathPattern.stringify(pathParts);

        window.history.pushState({}, "", newPathname + window.location.search);
    }
}

export function setInstanceToPath(processGuids: string[], stateFilter?: ProcessInstanceStateFilter) {
    const location: Location = Object.assign({}, window.location);
    const pathParts: PathParts = pathPattern.match(location.pathname);
    const searchParams = qs.parse(location.search.replace("?", ""));
    pathParts.processGuid = "instances";
    const newPathname = pathPattern.stringify(pathParts);
    let newSearch = qs.stringify(Object.assign({}, { processGuids: processGuids }, { instanceListStateFilter: stateFilter }, searchParams), { skipNulls: true, indices: false });
    newSearch = newSearch !== "" ? ("?" + newSearch) : "";
    window.history.pushState({}, "", newPathname + newSearch);
}

export function clearInstanceFromPath() {
    const location: Location = Object.assign({}, window.location);
    const pathParts: PathParts = pathPattern.match(location.pathname);
    pathParts.processGuid = null;
    const newPathname = pathPattern.stringify(pathParts);

    const searchParams = qs.parse(location.search.replace("?", ""));
    let newSearch = qs.stringify(_.omit(searchParams, ["instanceListStateFilter"], ["processGuids"]), { skipNulls: true });
    newSearch = newSearch !== "" ? ("?" + newSearch) : "";

    window.history.pushState({}, "", newPathname + newSearch);
}

export function getInstancePath(processGuid: string | undefined, stateFilter?: ProcessInstanceStateFilter): string {
    const location: Location = Object.assign({}, window.location);
    const pathParts: PathParts = pathPattern.match(location.pathname);
    pathParts.processGuid = processGuid;
    return pathPattern.stringify(pathParts) + (stateFilter != null ? ("?instanceListStateFilter=" + stateFilter) : "");
}

export function getProcessPath(processId: number, isSubprocess: boolean, environment?: string, agentGroup?: string): string {
    const location: Location = Object.assign({}, window.location);
    const pathParts: PathParts = pathPattern.match(location.pathname);
    pathParts.listType = isSubprocess ? ProcessListType[ProcessListType.Subprocess] : ProcessListType[ProcessListType.Process];
    pathParts.environmentName = environment || pathParts.environmentName;
    pathParts.agentGroupName = agentGroup || pathParts.agentGroupName;
    return pathPattern.stringify(pathParts) + "?highlightProcess=" + processId;
}

export function getSearchParameters(): ListSearchParameters {
    const searchString = location.search && location.search.substring(1); // strip leading '?'
    return getSearchParametersFromString(searchString);
}

export function setSearchParameters(parameters: ListSearchParameters) {
    const newSearchString = qs.stringify(parameters, { skipNulls: true, arrayFormat: "repeat" });

    window.history.pushState({}, "", window.location.pathname + "?" + newSearchString);
}

export function setSearchParameter(key: keyof (ListSearchParameters), value: string | null) {
    const currentSearchParameters = getSearchParameters();
    const newSearchParameters = Object.assign({}, currentSearchParameters, { [key]: value });
    setSearchParameters(newSearchParameters);
}

export function getInitialSearchState(): Partial<ListState> {
    const initialStateFromUri = getSearchParameters();
    const pathParts: PathParts = pathPattern.match(location.pathname);

    let countsTimeLimitDays = defaultCountsTimeLimitDays;
    if (filterIsOnCountsAndOlderThanCurrentTimeLimit(initialStateFromUri.filterCategory, initialStateFromUri.filterText, defaultCountsTimeLimitDays)) {
        countsTimeLimitDays = 0;
    }

    // Migration folder older style of process guid for instance list where guid was in path
    let instanceProcessGuids: IFilterType = null;
    if (pathParts.processGuid) {
        instanceProcessGuids = initialStateFromUri.processGuids || pathParts.processGuid;
        if (!Array.isArray(instanceProcessGuids)) {
            instanceProcessGuids = [instanceProcessGuids] as string[] | qs.ParsedQs[];
        }
    }

    return {
        // if there is a process to highlight we will ignore the current page as that is set in the component
        currentPage: initialStateFromUri.highlightProcess ? null : (initialStateFromUri.page || 1),
        highlightProcess: initialStateFromUri.highlightProcess,
        sortBy: initialStateFromUri.sortBy as keyof (ProcessWithStatus) || "name",
        sortDescending: initialStateFromUri.desc || false,
        filterText: initialStateFromUri.filterText,
        filterCategory: initialStateFromUri.filterCategory,
        instancesPageProcessGuids: instanceProcessGuids,
        startTime: initialStateFromUri.startTime,
        endTime: initialStateFromUri.endTime,
        instanceListStateFilter: initialStateFromUri.instanceListStateFilter,
        countsTimeLimitDays: countsTimeLimitDays
    };
}

export function filterIsOnCountsAndOlderThanCurrentTimeLimit(category: FilterCategory | null | string, filter: IFilterType | undefined, currentTimeLimit: number) {
    if (category === FilterCategory.ErrorsSince || category === FilterCategory.ExecutionsSince) {
        const filterDays = parseInt(filter as string, 10);
        return !_.isNaN(filterDays) && filterDays > currentTimeLimit;
    }
    return false;
}

export interface ProcessListSearchParams {
    page?: number | null;
    sortBy?: IFilterType;
    desc?: boolean | null | string;
    filterText?: IFilterType;
    highlightProcess?: number | null;
    filterCategory: FilterCategory | string | null;
}
export interface ListSearchParameters extends ProcessListSearchParams {
    // Instance list filters
    startTime?: IFilterType;
    endTime?: IFilterType;
    instanceListStateFilter: ProcessInstanceStateFilter | undefined;
    processGuids: IFilterType;
}

export type IFilterType = string | null | string[] | qs.ParsedQs | qs.ParsedQs[];
